import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getUsers } from "../../services/users";
import { getClubs } from "../../services/api";
import api from "../../services/api";
import { usePageTitle } from "../../hooks/usePageTitle";
import { useAuth } from "../../context/AuthContext";

type InvoiceStats = {
  unpaidCount: number;
  unpaidValue: number;
  totalRevenue: number;
  paidThisMonth: number;
  monthlyEarnings: { month: string; year: number; amount: number }[];
};

export default function AdminPlatforma() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { roles } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalClubs: 0,
    totalAthletes: 0,
    totalCoaches: 0,
    loading: true,
  });
  const [invoiceStats, setInvoiceStats] = useState<InvoiceStats>({
    unpaidCount: 0,
    unpaidValue: 0,
    totalRevenue: 0,
    paidThisMonth: 0,
    monthlyEarnings: [],
  });

  usePageTitle(t("dash.platform_admin", "Platform Admin"));

  // Check if user has platform_admin role with platform scope
  const hasPlatformAdmin = roles.some((r) => r.code === "platform_admin" && r.scope_type === "platform");

  useEffect(() => {
    // Redirect users without platform_admin role to dashboard (which will route them to correct page)
    if (roles.length > 0 && !hasPlatformAdmin) {
      navigate("/dashboard", { replace: true });
      return;
    }
    fetchStats();
    fetchInvoiceStats();
  }, [roles, hasPlatformAdmin, navigate]);

  const fetchStats = async () => {
    try {
      const [usersData, clubsData, athletesData, coachesData] = await Promise.all([
        getUsers({ pageSize: 1 }),
        getClubs(),
        getUsers({ role: "athlete", pageSize: 1 }),
        getUsers({ role: "coach", pageSize: 1 }),
      ]);

      setStats({
        totalUsers: usersData.total,
        totalClubs: clubsData.length,
        totalAthletes: athletesData.total,
        totalCoaches: coachesData.total,
        loading: false,
      });
    } catch (error) {
      console.error("Failed to fetch stats:", error);
      setStats((prev) => ({ ...prev, loading: false }));
    }
  };

  const fetchInvoiceStats = async () => {
    try {
      const { data } = await api.get<InvoiceStats>("/invoices/stats");
      setInvoiceStats(data);
    } catch (error) {
      console.error("Failed to fetch invoice stats:", error);
    }
  };

  const quickActions = [
    {
      title: t("dash.user_management", "User Management"),
      description: t("dash.user_management_desc", "Create, edit, and manage all platform users and their roles"),
      path: "/admin/users",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      color: "blue",
      enabled: true,
    },
    {
      title: t("dash.club_management", "Club Management"),
      description: t("dash.club_management_desc", "Create and manage clubs, teams, and club memberships"),
      path: "/admin/clubs",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      color: "emerald",
      enabled: true,
    },
    {
      title: t("dash.quiz_management", "Quiz Management"),
      description: t("dash.quiz_management_desc", "Create and manage educational quizzes with media support"),
      path: "/admin/quizzes",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
      color: "purple",
      enabled: true,
    },
    {
      title: t("dash.points_config", "Points Configuration"),
      description: t("dash.points_config_desc", "Configure point values for attendance, quizzes, and other actions"),
      path: "/admin/points",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: "amber",
      enabled: true,
    },
    {
      title: t("dash.reports_analytics", "Reports & Analytics"),
      description: t("dash.reports_analytics_desc", "View platform-wide reports and analytics"),
      path: "/admin/reports",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      color: "cyan",
      enabled: true,
    },
    {
      title: t("dash.billing_subscriptions", "Billing & Subscriptions"),
      description: t("dash.billing_subscriptions_desc", "Manage club subscriptions and billing"),
      path: "/admin/billing",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
      color: "rose",
      enabled: true,
    },
    {
      title: t("dash.notifications", "Notifications"),
      description: t("dash.notifications_desc", "Manage platform notifications and announcements"),
      path: "/admin/notifications",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
      color: "orange",
      enabled: true,
    },
    {
      title: t("dash.settings", "Settings"),
      description: t("dash.settings_desc", "Configure platform settings and preferences"),
      path: "/admin/settings",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      color: "indigo",
      enabled: true,
    },
    {
      title: t("dash.support", "Support"),
      description: t("dash.support_desc", "Manage support tickets and user feedback"),
      path: "/admin/support",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      color: "teal",
      enabled: true,
    },
  ];

  const getColorClasses = (color: string, enabled: boolean) => {
    if (!enabled) {
      return {
        bg: "bg-slate-100",
        text: "text-slate-400",
        border: "border-slate-200",
        hover: "",
      };
    }
    const colors: Record<string, { bg: string; text: string; border: string; hover: string }> = {
      blue: { bg: "bg-blue-50", text: "text-blue-600", border: "border-blue-200", hover: "hover:border-blue-400 hover:shadow-blue-100" },
      emerald: { bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-200", hover: "hover:border-emerald-400 hover:shadow-emerald-100" },
      purple: { bg: "bg-purple-50", text: "text-purple-600", border: "border-purple-200", hover: "hover:border-purple-400 hover:shadow-purple-100" },
      amber: { bg: "bg-amber-50", text: "text-amber-600", border: "border-amber-200", hover: "hover:border-amber-400 hover:shadow-amber-100" },
      cyan: { bg: "bg-cyan-50", text: "text-cyan-600", border: "border-cyan-200", hover: "hover:border-cyan-400 hover:shadow-cyan-100" },
      rose: { bg: "bg-rose-50", text: "text-rose-600", border: "border-rose-200", hover: "hover:border-rose-400 hover:shadow-rose-100" },
      orange: { bg: "bg-orange-50", text: "text-orange-600", border: "border-orange-200", hover: "hover:border-orange-400 hover:shadow-orange-100" },
      indigo: { bg: "bg-indigo-50", text: "text-indigo-600", border: "border-indigo-200", hover: "hover:border-indigo-400 hover:shadow-indigo-100" },
      teal: { bg: "bg-teal-50", text: "text-teal-600", border: "border-teal-200", hover: "hover:border-teal-400 hover:shadow-teal-100" },
      slate: { bg: "bg-slate-100", text: "text-slate-500", border: "border-slate-200", hover: "" },
    };
    return colors[color] || colors.slate;
  };

  // Period selection for earnings chart
  const [selectedPeriod, setSelectedPeriod] = useState<"3m" | "6m" | "12m">("6m");

  // Get earnings data based on selected period
  const getEarningsForPeriod = () => {
    const earnings = invoiceStats.monthlyEarnings || [];
    if (selectedPeriod === "3m") return earnings.slice(-3);
    if (selectedPeriod === "6m") return earnings.slice(-6);
    return earnings;
  };

  const currentEarnings = getEarningsForPeriod();
  const maxEarning = Math.max(...currentEarnings.map((e) => e.amount), 1);

  if (stats.loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-slate-600 font-medium">Se încarcă...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="mb-8 bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-800">
                {t("dash.platform_admin", "Platform Admin Dashboard")}
              </h1>
              <p className="text-slate-500 mt-1">
                {t("dash.platform_admin_description", "Manage the entire iGROWth platform")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg font-medium">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
              {t("dash.online", "Online")}
            </span>
            <span className="text-slate-400">•</span>
            <span className="text-slate-500">{t("dash.last_updated", "Actualizat acum")}</span>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-50 rounded-xl">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Total</span>
            </div>
            <p className="text-3xl font-bold text-slate-800 mb-1">
              {stats.totalUsers.toLocaleString()}
            </p>
            <p className="text-sm text-slate-500">
              {t("dash.total_users", "Total Users")}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-emerald-50 rounded-xl">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Active</span>
            </div>
            <p className="text-3xl font-bold text-slate-800 mb-1">
              {stats.totalClubs.toLocaleString()}
            </p>
            <p className="text-sm text-slate-500">
              {t("dash.total_clubs", "Total Clubs")}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-50 rounded-xl">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Registered</span>
            </div>
            <p className="text-3xl font-bold text-slate-800 mb-1">
              {stats.totalAthletes.toLocaleString()}
            </p>
            <p className="text-sm text-slate-500">
              {t("dash.total_athletes", "Total Athletes")}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-amber-50 rounded-xl">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Active</span>
            </div>
            <p className="text-3xl font-bold text-slate-800 mb-1">
              {stats.totalCoaches.toLocaleString()}
            </p>
            <p className="text-sm text-slate-500">
              {t("dash.total_coaches", "Total Coaches")}
            </p>
          </div>
        </div>

        {/* Income & Invoices Section */}
        <div className="mb-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Invoice Stats */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">
              {t("dash.invoice_summary", "Invoice Summary")}
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl border border-red-100">
                <div>
                  <p className="text-sm text-slate-600">{t("dash.unpaid_invoices", "Unpaid Invoices")}</p>
                  <p className="text-2xl font-bold text-red-600">{invoiceStats.unpaidCount}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-xl">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl border border-amber-100">
                <div>
                  <p className="text-sm text-slate-600">{t("dash.unpaid_value", "Unpaid Value")}</p>
                  <p className="text-2xl font-bold text-amber-600">{invoiceStats.unpaidValue.toLocaleString()} RON</p>
                </div>
                <div className="p-3 bg-amber-100 rounded-xl">
                  <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                <div>
                  <p className="text-sm text-slate-600">{t("dash.paid_this_month", "Paid This Month")}</p>
                  <p className="text-2xl font-bold text-emerald-600">{invoiceStats.paidThisMonth.toLocaleString()} RON</p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-xl">
                  <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Earnings Chart */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">
                  {t("dash.earnings_overview", "Earnings Overview")}
                </h3>
                <p className="text-sm text-slate-500">
                  {t("dash.total_revenue", "Total Revenue")}: {invoiceStats.totalRevenue.toLocaleString()} RON
                </p>
              </div>
              <div className="flex gap-2">
                {(["3m", "6m", "12m"] as const).map((period) => (
                  <button
                    key={period}
                    onClick={() => setSelectedPeriod(period)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      selectedPeriod === period
                        ? "bg-blue-600 text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {period === "3m" ? "3 luni" : period === "6m" ? "6 luni" : "12 luni"}
                  </button>
                ))}
              </div>
            </div>
            {/* Simple Bar Chart */}
            <div className="flex items-end gap-2 h-48">
              {currentEarnings.map((data, index) => (
                <div key={index} className="flex-1 flex flex-col items-center gap-2">
                  <div
                    className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t-lg transition-all duration-300 hover:from-blue-700 hover:to-blue-500"
                    style={{
                      height: `${(data.amount / maxEarning) * 100}%`,
                      minHeight: "20px",
                    }}
                  >
                    <div className="opacity-0 hover:opacity-100 transition-opacity text-xs text-white text-center pt-1 font-medium">
                      {data.amount.toLocaleString()}
                    </div>
                  </div>
                  <span className="text-xs text-slate-500 font-medium">{data.month}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-blue-500 rounded"></span>
                  <span className="text-sm text-slate-600">{t("dash.monthly_earnings", "Monthly Earnings")}</span>
                </div>
              </div>
              <button
                onClick={() => navigate("/admin/billing")}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
              >
                {t("dash.view_details", "View Details")}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-800">
              {t("dash.quick_actions", "Quick Actions")}
            </h2>
            <span className="text-sm text-slate-500">
              {quickActions.filter(a => a.enabled).length} {t("dash.available", "disponibile")}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {quickActions.map((action, index) => {
              const colors = getColorClasses(action.color, action.enabled);
              return (
                <button
                  key={index}
                  onClick={() => action.enabled && navigate(action.path)}
                  disabled={!action.enabled}
                  className={`bg-white rounded-2xl border ${colors.border} p-5 text-left transition-all duration-200 ${
                    action.enabled
                      ? `${colors.hover} hover:shadow-lg cursor-pointer`
                      : "opacity-60 cursor-not-allowed"
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`p-2.5 ${colors.bg} rounded-xl`}>
                      <span className={colors.text}>{action.icon}</span>
                    </div>
                    {action.enabled ? (
                      <svg className={`w-5 h-5 ${colors.text} opacity-50`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    ) : (
                      <span className="px-2 py-1 bg-slate-100 text-slate-500 text-xs font-medium rounded-lg">
                        {t("common.coming_soon", "În curând")}
                      </span>
                    )}
                  </div>
                  <h3 className={`text-lg font-semibold mb-1.5 ${action.enabled ? "text-slate-800" : "text-slate-500"}`}>
                    {action.title}
                  </h3>
                  <p className="text-sm text-slate-500 line-clamp-2">
                    {action.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
}
